import React, { useState, useRef, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Box,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  ClickAwayListener,
  Popper,
  Fade
} from '@mui/material';

// Enhanced RHF Timezone Select
export function RHFTimezoneSelect({
  name,
  label,
  size = "large", 
  placeholder = "Select timezone",
  helperText,
  timezones,
  ...other
}) {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter timezones based on search
  const filteredTimezones = timezones.filter(tz => 
    tz.label.toLowerCase().includes(search.toLowerCase()) ||
    tz.tzCode.toLowerCase().includes(search.toLowerCase())
  );

  // Group timezones by region
  const groupedTimezones = filteredTimezones.reduce((groups, tz) => {
    // Extract region from timezone label (e.g., "America/New_York" -> "America")
    const region = tz.tzCode.split('/')[0];
    if (!groups[region]) {
      groups[region] = [];
    }
    groups[region].push(tz);
    return groups;
  }, {});

  // Sort regions alphabetically
  const sortedRegions = Object.keys(groupedTimezones).sort();

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 10);
    }
  }, [open]);

  // Handle click away
  const handleClickAway = () => {
    setOpen(false);
    setSearch('');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Find selected timezone label
        const selectedTimezone = timezones.find(tz => tz.tzCode === field.value);
        const displayValue = selectedTimezone ? selectedTimezone.label : placeholder;

        return (
          <Box sx={{ mb: 2, width: '100%' }}>
            {label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500, 
                  mb: 0.5, 
                  color: error ? 'error.main' : 'text.primary' 
                }}
              >
                {label}
              </Typography>
            )}

            {/* Custom select field */}
            <TextField
              inputRef={anchorRef}
              fullWidth
              value={displayValue}
              onClick={() => setOpen(prev => !prev)}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error.message : helperText}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Box 
                      component="span" 
                      sx={{ 
                        fontSize: '1.25rem', 
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'none'
                      }}
                    >
                      ▾
                    </Box>
                  </InputAdornment>
                ),
                sx: {
                  cursor: 'pointer',
                  height: size === 'large' ? 56 : 40,
                }
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)',
                  }
                }
              }}
              {...other}
            />

            {/* Dropdown menu */}
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              transition
              placement="bottom-start"
              style={{ width: anchorRef.current?.offsetWidth, zIndex: 1300 }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={200}>
                  <Paper 
                    elevation={6}
                    sx={{ 
                      mt: 1, 
                      overflow: 'hidden',
                      maxHeight: 350
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClickAway}>
                      <Box>
                        {/* Search field */}
                        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <TextField
                            inputRef={searchInputRef}
                            fullWidth
                            placeholder="Search timezone..."
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoComplete="off"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <span role="img" aria-label="search">🔍</span>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>

                        {/* Timezone list */}
                        <Box 
                          sx={{ 
                            maxHeight: 280, 
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              borderRadius: '4px',
                            },
                          }}
                        >
                          {sortedRegions.length > 0 ? (
                            sortedRegions.map(region => (
                              <Box key={region}>
                                {/* Region header */}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    px: 2,
                                    py: 0.75,
                                    backgroundColor: 'background.neutral',
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                  }}
                                >
                                  {region.toUpperCase()}
                                </Typography>

                                {/* Region timezones */}
                                {groupedTimezones[region].map((tz, index) => (
                                  <Box
                                    key={index}
                                    onClick={() => {
                                      field.onChange(tz.tzCode);
                                      setOpen(false);
                                      setSearch('');
                                    }}
                                    sx={{
                                      px: 2,
                                      py: 1,
                                      cursor: 'pointer',
                                      backgroundColor: field.value === tz.tzCode ? 'action.selected' : 'transparent',
                                      '&:hover': {
                                        backgroundColor: 'action.hover',
                                      },
                                      '&:last-child': {
                                        borderBottomLeftRadius: 8,
                                        borderBottomRightRadius: 8,
                                      },
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2">{tz.label}</Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {tz.tzCode}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            ))
                          ) : (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No timezones found
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </ClickAwayListener>
                  </Paper>
                </Fade>
              )}
            </Popper>

            {/* Hidden native select for form submission */}
            <select
              {...field}
              style={{ display: 'none' }}
            >
              {timezones.map(({ tzCode, label }, index) => (
                <option key={index} value={tzCode}>
                  {label}
                </option>
              ))}
            </select>
          </Box>
        );
      }}
    />
  );
}

export default RHFTimezoneSelect;